/**
 * ResetPasswordPage — public route /reset-password?token=...
 * Validates token + sets new password. Auto-login on success.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, KeyRound, Check, AlertTriangle, ArrowLeft } from 'lucide-react';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

function formatErr(detail) {
  if (!detail) return 'कुछ गलत हो गया।';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e?.msg || JSON.stringify(e))).join(' ');
  return String(detail);
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ n: false, k: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('Reset link अमान्य है — token मौजूद नहीं।');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) { setError('नया पासवर्ड और पुष्टि पासवर्ड समान नहीं हैं।'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/auth/reset-password`,
        { token, new_password: next, confirm_password: confirm },
      );
      // 🔒 Do NOT persist token to localStorage — backend already set the
      // httpOnly cookie in the same response. See SEC audit Feb 07, 2026.
      void data;
      setDone(true);
      setTimeout(() => navigate('/admin/login'), 3000);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary cosmic-bg-alt text-text-main flex flex-col">
      <SEO title="नया पासवर्ड Set करें · ViratAstro Admin" noIndex />

      <div className="container-custom py-8 flex-1">
        <Link to="/admin/login" className="inline-flex items-center gap-2 text-gold/80 hover:text-gold mb-6 text-sm" data-testid="back-to-login-reset">
          <ArrowLeft className="w-4 h-4" /> Login Page
        </Link>

        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-3">
              <KeyRound className="w-7 h-7 text-gold" />
            </div>
            <h1 className="font-heading text-2xl">नया पासवर्ड Set करें</h1>
            <p className="text-text-muted text-sm mt-1">अपना नया, मज़बूत पासवर्ड दर्ज करें</p>
          </div>

          {done ? (
            <div className="glass-card p-6 text-center" data-testid="reset-success">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mx-auto mb-3">
                <Check className="w-7 h-7 text-emerald-300" />
              </div>
              <h2 className="font-heading text-lg text-emerald-300">पासवर्ड Reset पूर्ण</h2>
              <p className="text-text-muted text-sm mt-2">कृपया नए पासवर्ड से login करें। 3 सेकंड में redirect हो रहा है...</p>
            </div>
          ) : (
            <form onSubmit={submit} className="glass-card p-6 space-y-4" data-testid="reset-password-form">
              {[
                { key: 'n', val: next,    set: setNext,    label: 'नया पासवर्ड',    testid: 'reset-new-password-input' },
                { key: 'k', val: confirm, set: setConfirm, label: 'पुष्टि पासवर्ड', testid: 'reset-confirm-password-input' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs uppercase tracking-wider text-gold/80 font-semibold">{f.label}</label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted/60" />
                    <input
                      type={show[f.key] ? 'text' : 'password'}
                      value={f.val}
                      onChange={(e) => f.set(e.target.value)}
                      autoComplete="new-password"
                      data-testid={f.testid}
                      required
                      disabled={!token}
                      className="w-full bg-black/30 border border-white/15 rounded-lg pl-10 pr-10 py-2.5 text-text-main focus:outline-none focus:border-gold/50 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShow(s => ({ ...s, [f.key]: !s[f.key] }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted/60 hover:text-gold"
                      aria-label="Toggle password visibility"
                    >
                      {show[f.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}

              <PasswordStrengthMeter password={next} />

              {error && (
                <div className="bg-rose-500/15 border border-rose-400/40 rounded-lg p-3 text-rose-300 text-sm flex gap-2" data-testid="reset-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !token || !next || !confirm}
                data-testid="submit-reset"
                className="w-full btn-gold py-3 rounded-lg font-semibold disabled:opacity-50"
              >
                {loading ? 'Set किया जा रहा है...' : 'पासवर्ड Set करें'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
